import { reactive, computed } from 'vue'

/**
 * 动态序号注册表（模块级单例）
 *
 * 收集所有使用 SectionNumber 组件的板块，按章节号自动分组、自动重编号。
 * - 整数编号（如 01、02）为章节标题，保持不变
 * - 小数编号（如 4.1、4.2）按章节分组后，按原始编号排序后从 1 开始连续重编号
 * - hidden 的板块不参与编号，后续板块自动递补
 */

interface RegistryEntry {
  originalNo: string
  hidden: boolean
}

const registrations = reactive<Record<string, RegistryEntry>>({})

function parseChapter(no: string): string {
  return no.split('.')[0]
}

// 解析子序号：4.1 → 1, 4.10 → 10, 纯整数 → 0（排在最前）
function parseSubIndex(no: string): number {
  const parts = no.split('.')
  if (parts.length === 1) return 0 // 整数编号排最前
  return parseInt(parts[1]) || 0
}

const numbering = computed(() => {
  // 1. 按章节分组，过滤隐藏的
  const chapters: Record<string, string[]> = {}

  for (const [key, entry] of Object.entries(registrations)) {
    if (entry.hidden) continue
    const ch = parseChapter(entry.originalNo)
    if (!chapters[ch]) chapters[ch] = []
    chapters[ch].push(key)
  }

  // 2. 组内按原始编号排序后重编号
  const result: Record<string, string> = {}

  for (const [ch, keys] of Object.entries(chapters)) {
    // 按 originalNo 的子序号排序（确保 4.1, 4.2, 4.3 而不是按注册顺序）
    keys.sort((a, b) => {
      return parseSubIndex(registrations[a].originalNo) - parseSubIndex(registrations[b].originalNo)
    })

    let subIdx = 1
    for (const key of keys) {
      const original = registrations[key].originalNo
      if (!original.includes('.')) {
        // 整数编号（章节标题）保持不变
        result[key] = original
      } else {
        // 小数编号从 1 开始连续编号
        result[key] = ch + '.' + subIdx++
      }
    }
  }

  return result
})

export function registerSection(key: string, originalNo: string, hidden: boolean) {
  registrations[key] = { originalNo, hidden }
}

export function unregisterSection(key: string) {
  delete registrations[key]
}

export function updateSectionVisibility(key: string, hidden: boolean) {
  if (registrations[key]) {
    registrations[key].hidden = hidden
  }
}

export function resetSectionRegistry() {
  for (const key of Object.keys(registrations)) {
    delete registrations[key]
  }
}

export function useSectionRegistry() {
  return { numbering }
}
